import React, { useState, useEffect, useRef } from 'react';

/**
 * EMAIL STATUS TRACKER
 *
 * Track email delivery status through webhooks
 * See EVERYTHING about email lifecycle
 */

interface EmailStatus {
  messageId: string;
  to: string;
  from: string;
  subject: string;
  sentAt: Date;
  events: EmailEvent[];
  currentStatus: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained';
}

interface EmailEvent {
  id: string;
  type: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'rejected';
  timestamp: Date;
  details?: any;
  ip?: string;
  userAgent?: string;
}

interface WebhookPayload {
  type: string;
  created_at: string;
  data: {
    email_id: string;
    from: string;
    to: string[];
    subject: string;
    [key: string]: any;
  };
}

export default function EmailStatusTracker() {
  const [trackedEmails, setTrackedEmails] = useState<EmailStatus[]>([]);
  const [webhookEvents, setWebhookEvents] = useState<WebhookPayload[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailStatus | null>(null);
  const [testMode, setTestMode] = useState(true);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [webhookEvents]);

  const startListening = () => {
    setIsListening(true);

    // Simulate webhook events for demonstration
    if (testMode) {
      simulateEmailJourney();
    }
  };

  const simulateEmailJourney = () => {
    const messageId = `msg_${Date.now()}`;
    const email = `test${Math.floor(Math.random() * 1000)}@example.com`;

    // Create new tracked email
    const newEmail: EmailStatus = {
      messageId,
      to: email,
      from: 'no-reply@updates.futr.bet',
      subject: `Your FutrBet code: ${Math.floor(100000 + Math.random() * 900000)}`,
      sentAt: new Date(),
      events: [],
      currentStatus: 'sent'
    };

    setTrackedEmails(prev => [...prev, newEmail]);

    // Simulate webhook events over time
    const events = [
      { type: 'email.sent', delay: 0, status: 'sent' },
      { type: 'email.delivered', delay: 2000, status: 'delivered' },
      { type: 'email.opened', delay: 5000, status: 'opened' },
      { type: 'email.clicked', delay: 8000, status: 'clicked' }
    ];

    events.forEach(({ type, delay, status }) => {
      setTimeout(() => {
        const webhookEvent: WebhookPayload = {
          type,
          created_at: new Date().toISOString(),
          data: {
            email_id: messageId,
            from: newEmail.from,
            to: [newEmail.to],
            subject: newEmail.subject,
            ...(type === 'email.delivered' && {
              delivered_at: new Date().toISOString(),
              smtp_response: '250 2.0.0 OK'
            }),
            ...(type === 'email.opened' && {
              ip: '192.168.1.1',
              user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_0 like Mac OS X)',
              opened_at: new Date().toISOString()
            }),
            ...(type === 'email.clicked' && {
              link: 'https://futr.bet/verify',
              clicked_at: new Date().toISOString()
            })
          }
        };

        setWebhookEvents(prev => [...prev, webhookEvent]);

        // Update email status
        setTrackedEmails(prev => prev.map(e =>
          e.messageId === messageId
            ? {
                ...e,
                currentStatus: status as EmailStatus['currentStatus'],
                events: [...e.events, {
                  id: `evt_${Date.now()}`,
                  type: type.replace('email.', '') as EmailEvent['type'],
                  timestamp: new Date(),
                  details: webhookEvent.data
                }]
              }
            : e
        ));
      }, delay);
    });
  };

  const simulateBounce = () => {
    const messageId = `msg_bounce_${Date.now()}`;
    const email = 'nonexistent@invalid-domain.com';

    const newEmail: EmailStatus = {
      messageId,
      to: email,
      from: 'no-reply@updates.futr.bet',
      subject: 'Test Bounce Email',
      sentAt: new Date(),
      events: [],
      currentStatus: 'sent'
    };

    setTrackedEmails(prev => [...prev, newEmail]);

    setTimeout(() => {
      const bounceEvent: WebhookPayload = {
        type: 'email.bounced',
        created_at: new Date().toISOString(),
        data: {
          email_id: messageId,
          from: newEmail.from,
          to: [newEmail.to],
          subject: newEmail.subject,
          bounce_type: 'hard',
          bounce_description: 'The email account does not exist',
          smtp_response: '550 5.1.1 User unknown'
        }
      };

      setWebhookEvents(prev => [...prev, bounceEvent]);

      setTrackedEmails(prev => prev.map(e =>
        e.messageId === messageId
          ? {
              ...e,
              currentStatus: 'bounced',
              events: [...e.events, {
                id: `evt_${Date.now()}`,
                type: 'bounced',
                timestamp: new Date(),
                details: bounceEvent.data
              }]
            }
          : e
      ));
    }, 1500);
  };

  const getStatusColor = (status: EmailStatus['currentStatus']) => {
    const colors = {
      sent: 'text-yellow-400',
      delivered: 'text-blue-400',
      opened: 'text-green-400',
      clicked: 'text-purple-400',
      bounced: 'text-red-400',
      complained: 'text-orange-400'
    };
    return colors[status] || 'text-gray-400';
  };

  const getStatusIcon = (status: EmailStatus['currentStatus']) => {
    const icons = {
      sent: '📤',
      delivered: '📬',
      opened: '📧',
      clicked: '🔗',
      bounced: '❌',
      complained: '⚠️'
    };
    return icons[status] || '•';
  };

  const getEventTypeColor = (type: string) => {
    if (type.includes('sent')) return 'border-yellow-400';
    if (type.includes('delivered')) return 'border-blue-400';
    if (type.includes('opened')) return 'border-green-400';
    if (type.includes('clicked')) return 'border-purple-400';
    if (type.includes('bounced')) return 'border-red-400';
    if (type.includes('complained')) return 'border-orange-400';
    return 'border-gray-400';
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">📊 EMAIL STATUS TRACKER</h1>
          <p className="text-gray-400">Track email delivery through webhook events</p>
          <p className="text-yellow-400 text-sm mt-2">See the complete email journey</p>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {/* Left: Controls */}
          <div className="space-y-4">
            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={startListening}
                  disabled={isListening}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isListening ? '👂 LISTENING...' : '🚀 START TRACKING'}
                </button>

                <button
                  onClick={simulateEmailJourney}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300"
                >
                  📧 SIMULATE EMAIL JOURNEY
                </button>

                <button
                  onClick={simulateBounce}
                  className="w-full bg-red-400 text-black p-3 hover:bg-red-300"
                >
                  ❌ SIMULATE BOUNCE
                </button>

                <label className="flex items-center gap-2 text-sm mt-4">
                  <input
                    type="checkbox"
                    checked={testMode}
                    onChange={(e) => setTestMode(e.target.checked)}
                  />
                  <span>Test Mode (simulated events)</span>
                </label>
              </div>
            </div>

            {/* Tracked Emails */}
            <div className="border border-cyan-400 p-4">
              <h2 className="text-xl mb-4 text-cyan-400">📮 TRACKED EMAILS</h2>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {trackedEmails.length === 0 ? (
                  <div className="text-gray-600 text-center py-4">No emails tracked yet</div>
                ) : (
                  trackedEmails.map((email) => (
                    <div
                      key={email.messageId}
                      onClick={() => setSelectedEmail(email)}
                      className={`border p-2 cursor-pointer hover:bg-gray-900 ${
                        selectedEmail?.messageId === email.messageId
                          ? 'border-green-400'
                          : 'border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {email.sentAt.toLocaleTimeString()}
                        </span>
                        <span className={`${getStatusColor(email.currentStatus)}`}>
                          {getStatusIcon(email.currentStatus)} {email.currentStatus}
                        </span>
                      </div>
                      <div className="text-xs mt-1">
                        <div className="text-gray-400 truncate">To: {email.to}</div>
                        <div className="text-gray-500 truncate">{email.subject}</div>
                      </div>
                      <div className="mt-2 flex gap-1">
                        {email.events.map((evt, i) => (
                          <span
                            key={i}
                            className={`inline-block w-2 h-2 rounded-full ${
                              evt.type === 'sent' ? 'bg-yellow-400' :
                              evt.type === 'delivered' ? 'bg-blue-400' :
                              evt.type === 'opened' ? 'bg-green-400' :
                              evt.type === 'clicked' ? 'bg-purple-400' :
                              evt.type === 'bounced' ? 'bg-red-400' :
                              'bg-gray-400'
                            }`}
                            title={evt.type}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Email Journey */}
            <div className="border border-purple-400 p-4">
              <h2 className="text-xl mb-4 text-purple-400">📍 EMAIL JOURNEY</h2>
              <div className="text-xs space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-yellow-400">📤</span>
                  <span>Sent - API accepts email</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-blue-400">📬</span>
                  <span>Delivered - Reached inbox</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400">📧</span>
                  <span>Opened - User viewed</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-purple-400">🔗</span>
                  <span>Clicked - Link clicked</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-red-400">❌</span>
                  <span>Bounced - Failed delivery</span>
                </div>
              </div>
            </div>
          </div>

          {/* Middle: Selected Email Details */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📧 EMAIL DETAILS</h2>

            {selectedEmail ? (
              <div className="space-y-4">
                <div className="bg-gray-900 p-3 rounded">
                  <div className="text-xs space-y-1">
                    <div><span className="text-gray-500">ID:</span> {selectedEmail.messageId}</div>
                    <div><span className="text-gray-500">To:</span> {selectedEmail.to}</div>
                    <div><span className="text-gray-500">From:</span> {selectedEmail.from}</div>
                    <div><span className="text-gray-500">Subject:</span> {selectedEmail.subject}</div>
                    <div><span className="text-gray-500">Sent:</span> {selectedEmail.sentAt.toLocaleString()}</div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm text-yellow-400">Event Timeline</h3>
                  {selectedEmail.events.map((event, i) => (
                    <div key={event.id} className="border-l-2 border-gray-600 pl-3 ml-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {event.timestamp.toLocaleTimeString()}
                        </span>
                        <span className={`text-xs font-bold ${
                          event.type === 'sent' ? 'text-yellow-400' :
                          event.type === 'delivered' ? 'text-blue-400' :
                          event.type === 'opened' ? 'text-green-400' :
                          event.type === 'clicked' ? 'text-purple-400' :
                          event.type === 'bounced' ? 'text-red-400' :
                          'text-gray-400'
                        }`}>
                          {event.type.toUpperCase()}
                        </span>
                      </div>
                      {event.details && (
                        <pre className="text-xs text-gray-500 mt-1 overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                        </pre>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-gray-600 text-center py-8">
                Select an email to see details
              </div>
            )}
          </div>

          {/* Right: Webhook Events */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">🪝 WEBHOOK EVENTS</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {webhookEvents.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Click "Start Tracking" to see webhook events...
                </div>
              ) : (
                webhookEvents.map((event, i) => (
                  <div
                    key={i}
                    className={`border-l-2 pl-2 ${getEventTypeColor(event.type)}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.created_at).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className="font-bold text-green-400">
                            {event.type}
                          </span>
                        </div>
                        <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Stats */}
        {trackedEmails.length > 0 && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">📈 EMAIL STATS</h2>
            <div className="grid grid-cols-6 gap-4 text-sm">
              <div className="text-center">
                <div className="text-2xl text-yellow-400">
                  {trackedEmails.length}
                </div>
                <div className="text-gray-400">Sent</div>
              </div>
              <div className="text-center">
                <div className="text-2xl text-blue-400">
                  {trackedEmails.filter(e => e.currentStatus === 'delivered' ||
                    e.currentStatus === 'opened' || e.currentStatus === 'clicked').length}
                </div>
                <div className="text-gray-400">Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-2xl text-green-400">
                  {trackedEmails.filter(e => e.currentStatus === 'opened' ||
                    e.currentStatus === 'clicked').length}
                </div>
                <div className="text-gray-400">Opened</div>
              </div>
              <div className="text-center">
                <div className="text-2xl text-purple-400">
                  {trackedEmails.filter(e => e.currentStatus === 'clicked').length}
                </div>
                <div className="text-gray-400">Clicked</div>
              </div>
              <div className="text-center">
                <div className="text-2xl text-red-400">
                  {trackedEmails.filter(e => e.currentStatus === 'bounced').length}
                </div>
                <div className="text-gray-400">Bounced</div>
              </div>
              <div className="text-center">
                <div className="text-2xl text-gray-400">
                  {trackedEmails.length > 0
                    ? Math.round(trackedEmails.filter(e => e.currentStatus === 'opened' ||
                        e.currentStatus === 'clicked').length / trackedEmails.length * 100)
                    : 0}%
                </div>
                <div className="text-gray-400">Open Rate</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}